import { useQuery } from '@tanstack/react-query';
import type { CompanyListItem } from '@erp/rpc';
import { orpc } from '@/api/rpc/client';
import { useDebounce } from '@/hooks/useDebounce';
import { listCompaniesInput } from '@/components/company/useCompanyPicker';
import {
  explodePricelistPickerCompanies,
  rankPricelistPickerOptions,
} from '@/components/company/companyPickerDisplay';
import type { PricelistCaseVendorOption } from './pricelistCaseVendors';

/** A picker row as a case vendor option: the company at its one vendor#. */
export function pricelistCaseVendorOptionFromCompany(
  company: CompanyListItem,
): PricelistCaseVendorOption {
  return {
    companyId: company.id,
    companyName: company.name,
    vendorNumber: company.vendorNumbers[0] ?? null,
  };
}

/**
 * The vendor# rows a search offers on a price change: the server-side
 * Item Master company search (`listCompanies` with `forPricelistManagement`),
 * one row per vendor#, ranked exact > prefix > other (GEN-3073).
 */
export function usePricelistCaseVendorOptions(
  search: string,
): PricelistCaseVendorOption[] {
  const debouncedSearch = useDebounce(search, 300);
  const { data } = useQuery(
    orpc.core.companies.listCompanies.queryOptions({
      input: listCompaniesInput(true, debouncedSearch),
    }),
  );
  return rankPricelistPickerOptions(
    explodePricelistPickerCompanies(data?.companies ?? []),
    debouncedSearch,
  ).map(pricelistCaseVendorOptionFromCompany);
}
